<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick, watch } from 'vue';

const props = defineProps({
  conversationLabels: {
    type: Array,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const containerRef = ref(null);
const isDark = ref(document.body.classList.contains('dark'));
let darkObs = null;

// Quando expandido, o container vira flex-wrap e exibe todas as labels.
const expanded = ref(false);

onMounted(() => {
  darkObs = new MutationObserver(() => {
    isDark.value = document.body.classList.contains('dark');
  });
  darkObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  nextTick(() => recalculate());
});
onBeforeUnmount(() => { if (darkObs) darkObs.disconnect(); });

const activeLabels = computed(() => {
  const labelSet = new Set(props.conversationLabels);
  return props.accountLabels?.filter(({ title }) => labelSet.has(title)) || [];
});

const maxVisible = ref(20);
const showOverflow = ref(false);

function recalculate() {
  if (!containerRef.value) return;
  if (expanded.value) return; // sem cálculo quando expandido
  const container = containerRef.value;
  const containerWidth = container.offsetWidth;
  const badgeWidth = 52;
  const pills = container.querySelectorAll('.sz-cl-pill');
  if (!pills.length) return;

  let totalWidth = 0;
  let visible = 0;

  for (let i = 0; i < pills.length; i++) {
    const pillWidth = pills[i].offsetWidth + 4;
    if (totalWidth + pillWidth > containerWidth - (i < pills.length - 1 ? badgeWidth : 0)) {
      break;
    }
    totalWidth += pillWidth;
    visible++;
  }

  if (visible < activeLabels.value.length && visible > 0) {
    maxVisible.value = visible;
    showOverflow.value = true;
  } else {
    maxVisible.value = activeLabels.value.length;
    showOverflow.value = false;
  }
}

const visibleLabels = computed(() =>
  expanded.value ? activeLabels.value : activeLabels.value.slice(0, maxVisible.value)
);

const hiddenCount = computed(() =>
  Math.max(0, activeLabels.value.length - maxVisible.value)
);

watch(activeLabels, () => {
  maxVisible.value = 20;
  showOverflow.value = false;
  expanded.value = false;
  nextTick(() => nextTick(() => recalculate()));
}, { deep: true });

function toggleExpanded(evt) {
  // Importante: previne o handler de clique do card pai (que abre a conversa).
  evt.stopPropagation();
  evt.preventDefault();
  expanded.value = !expanded.value;
  if (!expanded.value) {
    nextTick(() => recalculate());
  }
}
</script>

<template>
  <div
    ref="containerRef"
    v-resize="recalculate"
    class="sz-cl-container"
    :class="{ 'sz-cl-container--expanded': expanded }"
  >
    <div class="sz-cl-pills">
      <div
        v-for="label in visibleLabels"
        :key="label.id"
        class="sz-cl-pill"
        :style="{
          background: label.color + '1A',
          color: label.color,
        }"
      >
        <span
          class="sz-cl-dot"
          :style="{ background: label.color }"
        />
        <span class="sz-cl-title">{{ label.title }}</span>
      </div>

      <!-- Botão recolher (inline, no fim das pills) -->
      <button
        v-if="expanded && activeLabels.length > 1"
        type="button"
        class="sz-cl-collapse"
        :class="{ dark: isDark }"
        title="Recolher etiquetas"
        @click="toggleExpanded"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M6 15l6-6 6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>

    <!-- Fade gradient + badge +N (só quando colapsado e há overflow) -->
    <div
      v-if="!expanded && showOverflow && hiddenCount > 0"
      class="sz-cl-fade"
      :class="{ dark: isDark }"
    >
      <button
        type="button"
        class="sz-cl-badge"
        @click="toggleExpanded"
      >
        <span>+{{ hiddenCount }}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.sz-cl-container {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  height: 22px;
  overflow: hidden;
}
/* Quando expandido, sai do limite de 22px e quebra linha. */
.sz-cl-container--expanded {
  height: auto;
  overflow: visible;
  align-items: flex-start;
}
.sz-cl-pills {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: nowrap;
  min-width: 0;
}
.sz-cl-container--expanded .sz-cl-pills {
  flex-wrap: wrap;
  row-gap: 6px;
}
.sz-cl-pill {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 6px 2px 4px;
  height: 16px;
  border-radius: 29px;
  font-size: 11px;
  font-weight: 500;
  line-height: 100%;
  white-space: nowrap;
  flex-shrink: 0;
  /* SZ: limita largura individual da pill pra labels longas não monopolizarem
     a linha — assim cabe a sequência (Aberto > Acompanhar > Campanhas...) */
  max-width: 110px;
}
.sz-cl-dot {
  width: 9px;
  height: 9px;
  min-width: 9px;
  border-radius: 50%;
  flex-shrink: 0;
}
.sz-cl-title {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Fade gradient overlay (estado colapsado com overflow).
   A cor de fundo vem do card pai via CSS variable --sz-card-bg. Cada
   wrapper (ConversationCard normal / ContactPanelHistory current/normal)
   define a variável certa para combinar com o fundo real do card. */
.sz-cl-fade {
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding-left: 32px;
  padding-right: 0;
  background: linear-gradient(
    270deg,
    var(--sz-card-bg, #FFFFFF) 50%,
    transparent 100%
  );
  pointer-events: none;
}
.sz-cl-fade.dark {
  background: linear-gradient(
    270deg,
    var(--sz-card-bg, #1C1E22) 50%,
    transparent 100%
  );
}

/* Badge +N — agora é um <button> clicável que toggla a expansão. */
.sz-cl-badge {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px 4px;
  height: 16px;
  background: #E1EBDC;
  border-radius: 24px;
  font-size: 12px;
  font-weight: 600;
  color: #388E3C;
  line-height: 1;
  pointer-events: auto;
  flex-shrink: 0;
  border: 0;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.sz-cl-badge:hover { background: #D3E5CB; }
.sz-cl-fade.dark .sz-cl-badge {
  background: rgba(56,142,60,0.20);
  color: #4ADE80;
}
.sz-cl-fade.dark .sz-cl-badge:hover {
  background: rgba(56,142,60,0.30);
}

/* Botão recolher (inline, no estado expandido) */
.sz-cl-collapse {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 16px;
  padding: 0;
  border: 0;
  border-radius: 24px;
  background: #E1EBDC;
  color: #388E3C;
  cursor: pointer;
  flex-shrink: 0;
  transition: background-color 0.12s ease;
}
.sz-cl-collapse:hover { background: #D3E5CB; }
.sz-cl-collapse.dark {
  background: rgba(56,142,60,0.20);
  color: #4ADE80;
}
.sz-cl-collapse.dark:hover {
  background: rgba(56,142,60,0.30);
}
</style>
